import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent } from "@/lib/consent";
import { initAnalytics } from "@/lib/analytics";

// Transactional email-landing pages where a consent modal is pure friction (the
// visitor arrived from a link to do one thing). No non-essential cookies load
// here anyway until the user accepts elsewhere, so suppressing the banner keeps
// us PECR-compliant.
const SUPPRESSED_PATHS = new Set(["/unsubscribe"]);

/**
 * Cookie-consent banner for non-essential trackers (GA4 + Meta Pixel).
 *
 * UK PECR / UK GDPR: those tags must not load before opt-in, and rejecting must
 * be as easy as accepting (equal-weight buttons, no pre-ticked anything). On
 * accept we persist the choice and start analytics immediately; on reject we
 * persist and load nothing. Once a choice exists the banner never shows again.
 */
export const CookieConsent = () => {
  const { pathname } = useLocation();
  // Undecided => show. Render nothing until mounted so SSR/first paint is clean.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setShow(true);
  }, []);

  if (!show || SUPPRESSED_PATHS.has(pathname)) return null;

  const accept = () => {
    setConsent("accepted");
    initAnalytics(); // self-gates on the stored choice we just wrote
    setShow(false);
  };

  const reject = () => {
    setConsent("rejected");
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      // A bottom bar blended into large landing-page viewports and got missed, so
      // opt-in (and ad tracking) suffered. A soft-scrim centred card is the one
      // pattern that stays visible regardless of screen size without hijacking the
      // whole page. The scrim only dims — it does NOT force a choice (no forced
      // action, Reject as easy as Accept: UK PECR). Mobile drops to a bottom sheet.
      className="fixed inset-0 z-[150] flex items-end justify-center bg-foreground/40 p-0 backdrop-blur-[2px] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 sm:items-center sm:p-6"
    >
      <div
        className="w-full max-w-lg border-t-4 border-secondary bg-background p-6 shadow-2xl motion-safe:animate-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-500 sm:rounded-2xl sm:border-t-0 sm:p-8"
      >
        <div className="flex flex-col items-center gap-4 text-center sm:gap-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
            <Cookie className="h-7 w-7" />
          </span>
          <div className="space-y-1.5">
            <p className="text-lg font-semibold text-foreground sm:text-xl">
              We use cookies
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              We use cookies to measure site usage and improve Quizrun.{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Privacy &amp; Cookies
              </Link>
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 pt-1 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              onClick={reject}
              className="flex-1 sm:min-w-[140px] sm:flex-none"
            >
              Reject
            </Button>
            <Button
              onClick={accept}
              className="flex-1 sm:min-w-[140px] sm:flex-none"
            >
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
